<template>
  <form data-vv-scope="rolePermissions" class="space-y-16" @submit.prevent="submitPermissions">
    <div class="space-y-4">
      <div class="rounded bg-white border overflow-hidden">
        <BaseDataTable :data="permissionsByFeature">
          <template #header>
            <tr>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Feature
              </th>
              <th
                v-for="group in actionGroups"
                :key="group.name"
                scope="col"
                class="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center border-l"
              >
                {{ group.label }}
              </th>
              <th
                v-if="canEdit"
                scope="col"
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-0 border-l"
              />
            </tr>
            <tr v-if="canEdit">
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <BaseFormSelect v-model="featureFilter" :options="featureOptions" />
              </th>
              <th
                v-for="group in actionGroups"
                :key="group.name"
                scope="col"
                class="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center border-l"
              >
                <BaseButton
                  v-if="hasSomeActionGroupPermissionsSelected(group)"
                  type="button"
                  primary
                  outline
                  small
                  :disabled="featureFilter !== null"
                  @click="selectAllActionGroupPermissions(group)"
                >
                  All
                </BaseButton>
                <BaseButton
                  v-else
                  type="button"
                  primary
                  outline
                  small
                  :disabled="featureFilter !== null"
                  @click="deselectAllActionGroupPermissions(group)"
                >
                  None
                </BaseButton>
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-0 border-l"
              >
                <BaseButton
                  v-if="hasSomePermissionsSelected()"
                  type="button"
                  primary
                  outline
                  small
                  :disabled="featureFilter !== null"
                  @click="selectAllPermissions"
                >
                  All
                </BaseButton>
                <BaseButton
                  v-else
                  type="button"
                  primary
                  outline
                  small
                  :disabled="featureFilter !== null"
                  @click="deselectAllPermissions"
                >
                  None
                </BaseButton>
              </th>
            </tr>
          </template>

          <template #default="slotProps">
            <tr class="even:bg-white odd:bg-gray-50">
              <td class="px-4 py-4 text-sm text-gray-500">
                {{ slotProps.item.name }}
              </td>
              <td
                v-for="group in actionGroups"
                :key="`${slotProps.index}_${group.name}`"
                class="px-4 py-4 text-center border-l"
              >
                <label
                  v-if="group.name in slotProps.item.permissions"
                  class="label custom-checkbox"
                >
                  <input
                    type="checkbox"
                    @input="handleCheckbox($event, slotProps.item.permissions[group.name].id)"
                    :checked="ids.indexOf(slotProps.item.permissions[group.name].id) >= 0"
                    :disabled="processing || !canEdit"
                  />
                  <span />
                </label>
              </td>
              <td v-if="canEdit" class="px-4 py-4 text-right border-l">
                <BaseButton
                  v-if="hasSomeFeaturePermissionsSelected(slotProps.item)"
                  type="button"
                  primary
                  outline
                  small
                  @click="selectAllFeaturePermissions(slotProps.item)"
                >
                  All
                </BaseButton>
                <BaseButton
                  v-else
                  type="button"
                  primary
                  outline
                  small
                  @click="deselectAllFeaturePermissions(slotProps.item)"
                >
                  None
                </BaseButton>
              </td>
            </tr>
          </template>
        </BaseDataTable>
      </div>
    </div>
    <div v-if="canEdit" class="space-x-2 text-right">
      <button :disabled="processing" class="btn btn-brand px-6">
        {{ $t('submit') | capitalize }}
      </button>
    </div>
  </form>
</template>

<script>
import { rolePermissionMethods, rolePermissionComputed } from '@/store/adminHelper';
import { entityComputed } from '@/store/helper';
import { reduce, replace, snakeCase, startsWith, trim, uniq, xor } from 'lodash-es';

const actionGroups = {
  create: { name: 'create', label: 'Create' },
  read: { name: 'read', label: 'Read' },
  update: { name: 'update', label: 'Update' },
  delete: { name: 'delete', label: 'Delete' },
};

const actions = [
  {
    name: 'create',
    label: 'Create',
    group: actionGroups.create,
  },
  {
    name: 'read',
    label: 'Read',
    group: actionGroups.read,
  },
  {
    name: 'update',
    label: 'Update',
    group: actionGroups.update,
  },
  {
    name: 'edit',
    label: 'Edit',
    group: actionGroups.update,
  },
  {
    name: 'delete',
    label: 'Delete',
    group: actionGroups.delete,
  },
];

function findPermissionAction(permission) {
  const match = actions.find((action) => startsWith(permission.name, action.name));

  return match || null;
}

export default {
  props: {
    roleId: {
      type: [Number, String],
      required: true,
    },
    canEdit: Boolean,
  },
  data: () => ({
    fetching: false,
    updating: false,
    permissionIds: [],
    actions,
    actionGroups,
    featureFilter: null,
  }),
  computed: {
    ...rolePermissionComputed,
    ...entityComputed,
    processing() {
      return this.fetching || this.updating;
    },
    allPermissions() {
      return this.permissionIds.map((id) => this.entityById('permissions', id));
    },
    permissionsWithFeatureAndAction() {
      return this.allPermissions
        .map((permission) => {
          const action = findPermissionAction(permission);

          if (!action) {
            return false;
          }

          return {
            ...permission,
            action: action.name,
            action_group: action.group.name,
            feature: trim(replace(permission.name, action.name, '')),
          };
        })
        .filter(Boolean);
    },
    featureOptions() {
      return uniq(
        this.permissionsWithFeatureAndAction.map((permissions) => permissions.feature)
      ).map((item) => ({ label: item, value: item }));
    },
    filteredPermissionsWithFeatureAndAction() {
      if (!this.featureFilter) {
        return this.permissionsWithFeatureAndAction;
      }

      return this.permissionsWithFeatureAndAction.filter(
        (permission) => permission.feature === this.featureFilter
      );
    },
    permissionsByFeature() {
      return reduce(
        this.filteredPermissionsWithFeatureAndAction,
        (result, permission) => {
          const featureKey = snakeCase(permission.feature);
          const currentFeature = featureKey in result ? result[featureKey] : {};
          const action = findPermissionAction(permission);

          return {
            ...result,
            [featureKey]: {
              name: permission.feature,
              permissions: {
                ...(currentFeature?.permissions || {}),
                [action.group.name]: permission,
              },
            },
          };
        },
        {}
      );
    },
  },
  created() {
    this.fetching = true;
    const { roleId } = this;
    this.editPermissions({ roleId })
      .then(({ permissionIds }) => {
        this.permissionIds = permissionIds;
        this.fetching = false;
      })
      .catch(() => {
        this.fetching = false;
      });
  },
  methods: {
    ...rolePermissionMethods,
    handleCheckbox(ev, permissionId) {
      const { checked } = ev.target;
      if (checked) {
        this.setIds([...this.ids, permissionId]);
      } else {
        this.setIds([...this.ids.filter((id) => id !== permissionId)]);
      }
    },
    submitPermissions() {
      if (!this.canEdit) {
        return;
      }

      const { roleId, ids } = this;
      this.updating = true;
      this.updatePermissions({ roleId, permissionsData: { permissionIds: ids } })
        .then(() => {
          this.$refs.modal.closeModal();
          this.updating = false;
        })
        .catch(() => {
          this.updating = false;
        });
    },
    hasSomeFeaturePermissionsSelected(feature) {
      const permissionIds = Object.values(feature.permissions).map((permission) => permission.id);

      const allIncluded = uniq([...this.ids, ...permissionIds]);

      return allIncluded.length !== this.ids.length;
    },
    selectAllFeaturePermissions(feature) {
      const permissionIds = Object.values(feature.permissions).map((permission) => permission.id);

      this.setIds(uniq([...this.ids, ...permissionIds]));
    },
    deselectAllFeaturePermissions(feature) {
      const permissionIds = Object.values(feature.permissions).map((permission) => permission.id);

      this.setIds([...xor(this.ids, permissionIds)]);
    },
    hasSomeActionGroupPermissionsSelected(group) {
      const permissionIds = this.permissionsWithFeatureAndAction
        .filter((permission) => permission.action_group === group.name)
        .map((permission) => permission.id);

      const allIncluded = uniq([...this.ids, ...permissionIds]);

      return allIncluded.length !== this.ids.length;
    },
    selectAllActionGroupPermissions(group) {
      const permissionIds = this.permissionsWithFeatureAndAction
        .filter((permission) => permission.action_group === group.name)
        .map((permission) => permission.id);

      this.setIds(uniq([...this.ids, ...permissionIds]));
    },
    deselectAllActionGroupPermissions(group) {
      const permissionIds = this.permissionsWithFeatureAndAction
        .filter((permission) => permission.action_group === group.name)
        .map((permission) => permission.id);

      this.setIds([...xor(this.ids, permissionIds)]);
    },
    hasSomePermissionsSelected() {
      return this.permissionIds.length !== this.ids.length;
    },
    selectAllPermissions() {
      this.setIds([...this.permissionIds]);
    },
    deselectAllPermissions() {
      this.setIds([]);
    },
  },
};
</script>
